import { useFieldArray, useFormContext, Controller, useWatch } from 'react-hook-form';
import { FaExclamationTriangle, FaTrash } from 'react-icons/fa';
import styled from 'styled-components';

import { UserPermission } from 'data/__generated__';

import { TotemLabel } from 'components/TotemLabel';
import { Option, TotemSelect } from 'components/TotemSelect';
import { colors } from 'constants/colors';
import { TotemOrganizationSelect } from 'components/Select/TotemOrganizationSelect';

type PermissionField = {
    totemId: string | null;
    totemFullname: string | null;
    userGroups: UserPermission[];
};

const TOTOPS_PERMISSIONS = new Set([
    UserPermission.Deliverer,
    UserPermission.ExternalDeliverer,
    UserPermission.Preparer,
]);

const ALL_ROLE_OPTIONS: Option<UserPermission>[] = [
    { value: UserPermission.Admin, label: 'Admin' },
    { value: UserPermission.Analyst, label: 'Analyste' },
    { value: UserPermission.Deliverer, label: 'Livreur' },
    { value: UserPermission.Editor, label: 'Editeur' },
    { value: UserPermission.Employee, label: 'Employé(e)' },
    { value: UserPermission.ExternalDeliverer, label: 'Livreur externe' },
    { value: UserPermission.Preparer, label: 'Préparateur' },
    { value: UserPermission.Toteam, label: 'Toteam' },
    { value: UserPermission.Totech, label: 'Totech' },
    { value: UserPermission.Totest, label: 'Totest' },
];

export const PermissionsEditor = ({ disabled = false }: { disabled?: boolean }) => {
    const { control } = useFormContext<{ permissions: PermissionField[] }>();
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'permissions',
    });

    const usedTotemIds = fields.filter((f) => f.totemId != null).map((f) => f.totemId!);

    const watchedPermissions = useWatch({ control, name: 'permissions' });

    const globalIndex = fields.findIndex((f) => f.totemId == null);
    const globalGroups: UserPermission[] = watchedPermissions?.[globalIndex]?.userGroups ?? [];
    const siteFields = fields.map((field, index) => ({ field, index })).filter(({ field }) => field.totemId != null);

    return (
        <Container>
            <Section>
                <TotemLabel>Permissions globales (sur tous les totems)</TotemLabel>
                <Controller
                    name={`permissions.${globalIndex}.userGroups`}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <TotemSelect
                            isMulti
                            disabled={disabled}
                            value={value ?? []}
                            onChange={(selected) => onChange(selected)}
                            options={ALL_ROLE_OPTIONS}
                            placeholder="Rôles globaux"
                        />
                    )}
                />
            </Section>
            <Section>
                <TotemLabel>Permissions par totem</TotemLabel>
                {siteFields.map(({ field, index }) => {
                    const currentGroups: UserPermission[] = watchedPermissions?.[index]?.userGroups ?? [];
                    const duplicates = currentGroups.filter(
                        (g) => globalGroups.includes(g) && TOTOPS_PERMISSIONS.has(g),
                    );
                    const duplicateLabels = duplicates.map(
                        (d) => ALL_ROLE_OPTIONS.find((o) => o.value === d)?.label ?? d,
                    );

                    return (
                        <PermissionRowContainer key={field.id}>
                            <PermissionRow>
                                <PermissionFields>
                                    <TotemNameLabel title={field.totemFullname ?? field.totemId ?? ''}>
                                        {field.totemFullname ?? field.totemId}
                                    </TotemNameLabel>
                                    <Controller
                                        name={`permissions.${index}.userGroups`}
                                        control={control}
                                        render={({ field: { onChange, value } }) => (
                                            <TotemSelect
                                                isMulti
                                                disabled={disabled}
                                                value={value ?? []}
                                                onChange={(selected) => onChange(selected)}
                                                options={ALL_ROLE_OPTIONS}
                                                placeholder="Rôles"
                                            />
                                        )}
                                    />
                                </PermissionFields>
                                {!disabled && (
                                    <RemoveButton type="button" onClick={() => remove(index)}>
                                        <FaTrash size={13} />
                                    </RemoveButton>
                                )}
                            </PermissionRow>
                            {duplicates.length > 0 && (
                                <WarningMessage>
                                    <FaExclamationTriangle size={12} />
                                    Les autorisations {duplicateLabels.join(', ')} ont déjà accordé(s) globalement, et
                                    sont donc appliquées sur tous les totems. La configuration par totem n'aura pas
                                    d'effet ici.
                                </WarningMessage>
                            )}
                        </PermissionRowContainer>
                    );
                })}
                {!disabled && (
                    <AddTotemContainer>
                        <TotemOrganizationSelect
                            value={null}
                            onChange={(totem) => {
                                if (totem) {
                                    append({ totemId: totem._id, totemFullname: totem.fullname, userGroups: [] });
                                }
                            }}
                            excludedTotemIds={usedTotemIds}
                        />
                    </AddTotemContainer>
                )}
            </Section>
        </Container>
    );
};

const Container = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 24px;
    align-items: start;
`;

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
`;

const PermissionRowContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const PermissionRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
`;

const PermissionFields = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    gap: 4px;
`;

const WarningMessage = styled.span`
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    color: ${colors.orange};
`;

const TotemNameLabel = styled.span`
    font-weight: bold;
    color: ${colors.black};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const RemoveButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    color: ${colors.pureBlack};
    padding: 5px;
    margin-top: 22px;

    &:hover {
        opacity: 0.7;
    }
`;

const AddTotemContainer = styled.div`
    margin-top: 10px;
`;
